// The practice's imported patients waiting for review, opened from the nav. It opens on how the import went, for
// those who may see that, with the patients to review on a second tab. Each opens its review over the list, and
// drops off the list once merged or confirmed.
//
// The list starts with the patients the import couldn't create (no usable name and date of birth), whose records
// were kept aside; it can be narrowed to just those. Each opens over the list to add its records to a patient, and
// drops off the list once they are added. It can be narrowed to each group the progress counts the rest in too, and
// the progress's count of each group with patients on the list opens it there.
app.controller("patientImportListController", [
	"$scope",
	"$mdDialog",
	"Session",
	"toast",
	"PatientImportAPI",
	"PatientImportReview",
	"onCountChange",
	function ($scope, $mdDialog, Session, toast, PatientImportAPI, PatientImportReview, onCountChange) {
		const PAGE_SIZE = 25;

		// The list's filters: every patient to review, only those the import couldn't create, or one group of the rest
		// as the progress counts them. The icons are those the list marks each group's patients with.
		const FILTER_ALL = "ALL";
		const FILTER_NOT_CREATED = "NOT_CREATED";
		const FILTER_POSSIBLE_DUPLICATES = "POSSIBLE_DUPLICATES";
		const FILTER_UNFINISHED_MERGES = "UNFINISHED_MERGES";
		const FILTER_DETAILS_TO_COMPLETE = "DETAILS_TO_COMPLETE";
		$scope.filters = [
			{ key: FILTER_ALL, label: "All to review" },
			{ key: FILTER_NOT_CREATED, label: "Not created", icon: "fa-user-xmark" },
			{ key: FILTER_POSSIBLE_DUPLICATES, label: "Possible duplicates", icon: "fa-user-group" },
			{ key: FILTER_UNFINISHED_MERGES, label: "Unfinished merges", icon: "fa-code-merge" },
			{ key: FILTER_DETAILS_TO_COMPLETE, label: "Details to complete", icon: "fa-pen" },
		];

		$scope.loading = true;
		$scope.error = null;
		$scope.rows = [];
		$scope.total = 0;
		$scope.page = 0;
		$scope.filter = FILTER_ALL;
		// How many patients the import couldn't create are still to add to a patient, whichever the list shows.
		$scope.notCreated = 0;
		$scope.canViewUncreated = Session.hasPermission("PATIENT_IMPORT_UNCREATED_VIEW");

		$scope.canViewProgress = Session.hasPermission("PATIENT_IMPORT_STATS_VIEW");
		$scope.tab = $scope.canViewProgress ? "progress" : "list";
		$scope.progress = { loading: false, error: null, stats: null };

		$scope.close = function () {
			$mdDialog.hide();
		};

		// The progress is loaded when the dialog opens on it, and again each time its tab is opened, since reviews
		// done from the list change it.
		$scope.showTab = function (tab) {
			$scope.tab = tab;
			if (tab === "progress" && $scope.canViewProgress) {
				loadProgress();
			}
		};

		function loadProgress() {
			const progress = $scope.progress;
			progress.loading = !progress.stats;
			progress.error = null;
			PatientImportAPI.getStats()
				.then(function (resp) {
					progress.stats = toProgress(resp.data || {});
				})
				.catch(function () {
					progress.error = "The progress couldn't be loaded. Try again.";
				})
				.finally(function () {
					progress.loading = false;
				});
		}

		function toProgress(data) {
			const total = data.total || 0;
			// Imported successfully: matched or resolved already, or here with their name and date of birth and only
			// their other details to complete. Not: possible duplicates, and merges that stopped part-way. Those still
			// to review carry the list filter that shows them.
			const succeeded = [
				{ label: "Matched automatically", count: data.linked || 0 },
				{ label: "Details to complete", count: data.details_to_complete || 0, filter: FILTER_DETAILS_TO_COMPLETE },
				{ label: "Confirmed as new patients", count: data.confirmed || 0 },
				{ label: "Merged into existing patients", count: data.merged || 0 },
			];
			const failed = [
				{ label: "Possible duplicates", count: data.possible_duplicates || 0, filter: FILTER_POSSIBLE_DUPLICATES },
				{ label: "Unfinished merges", count: data.unfinished_merges || 0, filter: FILTER_UNFINISHED_MERGES },
			];
			// Left out when they couldn't be counted, rather than shown as none.
			if (data.not_created !== null && data.not_created !== undefined) {
				failed.push({ label: "Not created", count: data.not_created, filter: FILTER_NOT_CREATED });
			}
			const succeededCount = succeeded.reduce((sum, outcome) => sum + outcome.count, 0);
			return {
				raw: data,
				total: total,
				succeeded: succeeded,
				failed: failed,
				succeededCount: succeededCount,
				notCreated: data.not_created || 0,
				// Rounded down, so it never reads 100% while any have failed.
				percent: total ? Math.floor((succeededCount / total) * 100) : 0,
				firstImported: formatDate(data.first_imported_date),
				lastSeen: formatDate(data.last_seen_date),
				sources: (data.sources || []).map(function (source) {
					return {
						name: titleCase(source.source_system),
						total: source.total || 0,
						toReview: source.to_review || 0,
						firstImported: formatDate(source.first_imported_date),
						lastSeen: formatDate(source.last_seen_date),
					};
				}),
				resolvers: (data.resolvers || []).map(function (resolver) {
					return {
						name: resolver.name || "Former staff member",
						merged: resolver.merged || 0,
						confirmed: resolver.confirmed || 0,
						total: (resolver.merged || 0) + (resolver.confirmed || 0),
					};
				}),
			};
		}

		function formatDate(value) {
			return value && moment(value).isValid() ? moment(value).locale("en").format("D MMM YYYY") : "";
		}

		function titleCase(value) {
			return value
				? String(value).toLowerCase().split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
				: "";
		}

		// The one thing holding a patient back, in the order its review takes them: a merge already under way, then
		// possible duplicates, then its details (nothing else holds a patient back). Which details, or which
		// duplicates, is shown by the review. Coloured as the progress counts it: details to complete as a success,
		// the rest as failures.
		function toResolve(row) {
			if (row.status === "MERGING") {
				return { label: "Unfinished merge", icon: "fa-code-merge", failed: true };
			}
			return row.duplicate_count
				? { label: "Possible duplicate", icon: "fa-user-group", failed: true }
				: { label: "Details to complete", icon: "fa-pen", failed: false };
		}

		// A patient the import couldn't create, as the list shows them: what their record held of a name and date of
		// birth, which may be little or nothing.
		function toUncreatedRow(row) {
			const uncreated = row.uncreated || {};
			const identity = uncreated.identity || {};
			return {
				key: "uncreated-" + uncreated.id,
				uncreatedId: uncreated.id,
				name: [identity.first_name, identity.last_name].filter(Boolean).join(" "),
				dob: formatSourceDob(identity.date_of_birth),
				source: titleCase(row.source_system),
				imported: formatDate(row.imported_date),
				toResolve: { label: "Not created", icon: "fa-user-xmark", failed: true },
				action: "Check",
			};
		}

		// Previous systems keep dates of birth in their own formats; one that fits none is shown as it came, unless it
		// is a placeholder with no date in it at all ("NULL").
		function formatSourceDob(value) {
			const text = value ? String(value).trim() : "";
			const parsed = moment(text, ["YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss", "DD/MM/YYYY", "D/M/YYYY", "YYYY/MM/DD", "DD-MM-YYYY"], true);
			if (parsed.isValid()) {
				return parsed.locale("en").format("D MMM YYYY");
			}
			return /\d/.test(text) ? text : "";
		}

		function load() {
			$scope.loading = true;
			$scope.error = null;
			PatientImportAPI.getImportsToReview($scope.page, PAGE_SIZE, $scope.filter)
				.then(function (resp) {
					const data = resp.data || {};
					$scope.total = data.total || 0;
					$scope.notCreated = data.not_created || 0;
					// The last page emptied by reviews: step back to one that still has patients.
					if ($scope.page > 0 && !(data.imports || []).length && $scope.total) {
						$scope.page = Math.floor(($scope.total - 1) / PAGE_SIZE);
						return load();
					}
					$scope.rows = (data.imports || []).map(function (row) {
						if (row.status === "NOT_CREATED") {
							return toUncreatedRow(row);
						}
						const patient = row.patient || {};
						return {
							key: "patient-" + row.patient_id,
							patientId: row.patient_id,
							name: [patient.firstName, patient.lastName].filter(Boolean).join(" "),
							dob: formatDate(patient.dateOfBirth),
							source: titleCase(row.source_system),
							imported: formatDate(row.imported_date),
							toResolve: toResolve(row),
							action: "Review",
						};
					});
					// The nav counts every patient to review, which only the unfiltered list's total is.
					if ($scope.filter === FILTER_ALL) {
						onCountChange($scope.total);
					}
				})
				.catch(function () {
					$scope.error = "The imported patients couldn't be loaded. Try again.";
				})
				.finally(function () {
					$scope.loading = false;
				});
		}

		$scope.firstShown = function () {
			return $scope.page * PAGE_SIZE + 1;
		};

		$scope.lastShown = function () {
			return $scope.page * PAGE_SIZE + $scope.rows.length;
		};

		$scope.hasNextPage = function () {
			return $scope.lastShown() < $scope.total;
		};

		$scope.goToPage = function (page) {
			$scope.page = page;
			load();
		};

		$scope.setFilter = function (filter) {
			if (filter === $scope.filter && !$scope.loading) {
				return;
			}
			$scope.filter = filter;
			$scope.page = 0;
			load();
		};

		// Whether the list has a filter for this filter's patients, for this user.
		$scope.canFilter = function (filter) {
			return filter !== FILTER_NOT_CREATED || ($scope.canViewUncreated && (!!$scope.notCreated || $scope.filter === FILTER_NOT_CREATED));
		};

		// Whether a progress count opens its patients on the list: those still to review, when there are any.
		$scope.canShowOnList = function (outcome) {
			return !!outcome.filter && outcome.count > 0 && (outcome.filter !== FILTER_NOT_CREATED || $scope.canViewUncreated);
		};

		// From the progress: the list of just one group's patients.
		$scope.showOnList = function (filter) {
			$scope.tab = "list";
			$scope.setFilter(filter);
		};

		$scope.review = function (row) {
			if (row.uncreatedId) {
				PatientImportReview.openUncreated(row.uncreatedId)
					.then(function (patient) {
						const name = patient ? [patient.firstName, patient.lastName].filter(Boolean).join(" ") : "";
						toast.create({
							message: name ? "The records were added to " + name + "'s record." : "The records were added.",
							className: "alert-success",
							timeout: 6000,
							dismissible: true,
						});
						load();
					})
					.catch(angular.noop);
				return;
			}
			PatientImportReview.open(row.patientId).then(load).catch(angular.noop);
		};

		// The list is loaded behind the progress too, so the nav's count stays right.
		load();
		if ($scope.tab === "progress") {
			loadProgress();
		}
	},
]);
